import {
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  addToast,
} from "@heroui/react";
import { LimeCheckbox } from "@/Components/NextBase/LimeCheckbox";
import { LimeModal } from "@/Components/NextBase/LimeModal";
import { useClientFeature } from "@/hooks/useClientFeature";
import { Trans } from "@lingui/react/macro";
import { t } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import * as Sentry from "@sentry/react";
import { api } from "@/lib/api-client";
import { useState } from "react";
import {
  BILLING_DOCUMENT_OFF_HINT,
  UNSENT_DELIVERY,
} from "./billingDocumentMessages";
import { isBillingDocumentNotificationOn } from "./billingDocumentNotification";

type Props = {
  shareId: number | null;
  /** Display only: the copy differs for a company's share and the customer's. */
  payerCompanyId?: number | null;
  payerName?: string | null;
  onClose: () => void;
};

export function SubscriptionInvoiceIssueEstimateModal({
  shareId,
  payerCompanyId = null,
  payerName = null,
  onClose,
}: Props) {
  const [sendEstimateOnEmail, setSendEstimateOnEmail] = useState(false);

  const { mutateAsync: issueEstimate, isPending } =
    api.customer.useIssueCustomerSubscriptionEstimates();
  const { i18n } = useLingui();

  const isMemberShare = payerCompanyId == null;
  const { isFeatureEnabled } = useClientFeature();
  const { data: clientSettings } = api.client.useClientSettings({
    enabled: shareId != null && isMemberShare,
  });
  // For these clients "send" means the Billing document notification, not the Space Invoices email.
  const sendsNotification =
    isMemberShare &&
    clientSettings?.emailMemberEstimatesViaNotifications === true;
  const { data: notificationSettings } =
    api.notification.useGetNotificationSettings({
      enabled: sendsNotification,
    });
  const canSend =
    !sendsNotification ||
    isBillingDocumentNotificationOn({
      hasSubscriptionNotifications: isFeatureEnabled(
        "subscriptionNotificationSettings",
      ),
      notificationSettings,
    });

  const handleClose = () => {
    setSendEstimateOnEmail(false);
    onClose();
  };

  const handleConfirm = async () => {
    if (shareId == null) {
      Sentry.captureMessage(
        "SubscriptionInvoiceIssueEstimateModal: handleConfirm aborted — missing shareId",
        "warning",
      );
      return;
    }
    try {
      const { memberNotifications } = await issueEstimate({
        body: {
          shareIds: [shareId],
          sendEstimateOnEmail: sendEstimateOnEmail && canSend,
        },
      });
      addToast({
        title: t`Predračun je bil uspešno izdan`,
        color: "success",
      });
      for (const { delivery } of memberNotifications) {
        if (delivery === "sent") continue;
        addToast({
          title: t`Obvestilo stranki ni bilo poslano`,
          description: i18n._(UNSENT_DELIVERY[delivery]),
          color: "warning",
        });
      }
      handleClose();
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  return (
    <LimeModal
      isOpen={shareId != null}
      onClose={handleClose}
      data-identifier="subscription-invoice-issue-estimate-modal"
    >
      <ModalContent>
        {() => (
          <>
            <ModalHeader>
              <Trans>Izdaj predračun</Trans>
            </ModalHeader>
            <ModalBody>
              <div className="flex flex-col gap-3">
                <p className="text-default-700 text-sm">
                  {payerCompanyId != null && payerName ? (
                    <Trans>
                      Za delež podjetja {payerName} v tem obdobju bo izdan nov
                      predračun v davčni blagajni.
                    </Trans>
                  ) : (
                    <Trans>
                      Za to obdobje bo izdan nov predračun v davčni blagajni.
                    </Trans>
                  )}
                </p>
                <LimeCheckbox
                  size="sm"
                  isSelected={sendEstimateOnEmail && canSend}
                  isDisabled={!canSend}
                  onValueChange={setSendEstimateOnEmail}
                  data-identifier="send-estimate-on-email-checkbox"
                >
                  {payerCompanyId != null ? (
                    <Trans>Pošlji predračun po e-pošti podjetju</Trans>
                  ) : sendsNotification ? (
                    <Trans>Pošlji stranki obvestilo »Dokument obdobja«</Trans>
                  ) : (
                    <Trans>Pošlji predračun po e-pošti stranki</Trans>
                  )}
                </LimeCheckbox>
                {!canSend && (
                  <p
                    className="text-default-500 text-tiny"
                    data-identifier="send-estimate-notification-off-hint"
                  >
                    {i18n._(BILLING_DOCUMENT_OFF_HINT)}
                  </p>
                )}
              </div>
            </ModalBody>
            <ModalFooter>
              <Button variant="light" onPress={handleClose}>
                <Trans>Prekliči</Trans>
              </Button>
              <Button
                color="primary"
                onPress={handleConfirm}
                isLoading={isPending}
                data-identifier="confirm-issue-estimate-button"
              >
                <Trans>Izdaj predračun</Trans>
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </LimeModal>
  );
}
